import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ObjectUtils from '../../../../../../common/helpers/utils/object-utils';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useEmbeddedChatActions } from '@mm/state/actions/EmbeddedChatActions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { DEFAULT_WEBCHAT_INTERACTIVE_GREETING } from '../CustomerChatGreetingEditor/CustomerChatGreetingEditor';
import { ChatWidgetPreviewIFrameProps } from './ChatWidgetPreviewFrame';

export const ChatWidgetPreviewWebchatIFrame = (
  props: ChatWidgetPreviewIFrameProps,
) => {
  const {
    className,
    chatWidget,
    greetingWidget,
    showGreeting,
    onGreetingShown,
  } = props;
  const webchatHost = useSelector((state: StoreState) => state.env.webchatHost);
  const { chatWidgetChannel, webchatToken, noMatchResponse } = useSelector(
    activeChatbotSelector,
  );
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const { info: planInfo } = useSelector(activeFacebookPagePlanInfoSelector);

  const { getWebchatToken } = useChatbotCRUDActions();
  const { clearStorage } = useEmbeddedChatActions();

  useEffect(() => {
    clearStorage();
  }, [clearStorage]);

  // Listen for loaded
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data.event === 'webchat:greeting_shown') {
        onGreetingShown?.(e.data.bubbleShown);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  });

  useEffect(() => {
    if (chatWidgetChannel !== 'facebook') {
      getWebchatToken();
    }
  }, [chatWidgetChannel, getWebchatToken]);

  if (!webchatToken || !activeFacebookPage) {
    return null;
  }
  const promoter = {
    ...chatWidget,
    greeting:
      chatWidget.webchatGreeting ?? DEFAULT_WEBCHAT_INTERACTIVE_GREETING,
    greetingWidget: chatWidget.webchatGreetWithStartbot
      ? ObjectUtils.deepSnakeKeys(greetingWidget)
      : undefined,
    greetWithStartbot: chatWidget.webchatGreetWithStartbot,
    greetingDialogDisplay: showGreeting ? 'show' : 'hide',
    greetingDialogDisplayMobile: showGreeting ? 'show' : 'hide',
    noMatchResponse,
    pageName: activeFacebookPage.name,
    sendFirstMessageImmediately:
      !chatWidget.webchatGreetWithStartbot || !greetingWidget,
    notifyParentOnEvents: true,
    chatWidgetPreview: true,
  };

  if (showGreeting === 'without_delay') {
    promoter.disableAnimation = true;
    promoter.greetingDialogDelay = 0;
  }
  if (
    chatWidget.webchatGreetWithStartbot ||
    chatWidget.greetingStyle === 'static' ||
    !chatWidget.customButtonsEnabled
  ) {
    promoter.customButtons = chatWidget.customButtonsEnabled ? [] : undefined;
  }

  const srcDoc = `
    <html>
      <body>
        <script>
          window.mmAsyncInits = [function(config) {
            MMWebchat.bootstrap("${webchatToken}", ${JSON.stringify(promoter)});
          }];

        var tags = document.getElementsByTagName('script');
        var existingScriptTag = tags[0];
        window.mmAsyncSdkInit = function() {
          MMWebchat.initSdk({
            ${planInfo.isFreeOrSpecialPlan ? 'pow: "y"' : ''}
          });
        }
        js = document.createElement('script');
        js.src = "${webchatHost}webchat-bootstrap.js";
        existingScriptTag.parentNode.insertBefore(js, existingScriptTag);
        
        </script>
      </body>
    </html>`;
  return (
    <iframe className={className} title="Chat Widget Preview" srcDoc={srcDoc} />
  );
};
